import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  UserPlus,
  Copy,
  ArrowClockwise,
  XCircle,
} from '@phosphor-icons/react';
import { STATUS_CONFIG, formatDate } from './constants';

const EmptyState = ({ canManage, onCreate }) => (
  <div className="py-12 text-center" data-testid="invitations-empty-state">
    <UserPlus size={48} className="mx-auto mb-4 text-slate-300" />
    <p className="text-slate-500">No invitations yet</p>
    {canManage && (
      <Button
        variant="outline"
        className="mt-4"
        onClick={onCreate}
        data-testid="empty-state-create-btn"
      >
        Send First Invitation
      </Button>
    )}
  </div>
);

const InvitationRow = ({ invitation, onCopy, onRevoke, onResend }) => {
  const statusConfig = STATUS_CONFIG[invitation.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  return (
    <TableRow data-testid={`invitation-row-${invitation.id}`}>
      <TableCell className="font-medium">{invitation.name}</TableCell>
      <TableCell>{invitation.email}</TableCell>
      <TableCell>
        <Badge variant="outline">{invitation.role}</Badge>
      </TableCell>
      <TableCell className="text-sm">{formatDate(invitation.expires_at)}</TableCell>
      <TableCell>
        <Badge className={statusConfig.color}>
          <StatusIcon size={12} className="mr-1" />
          {statusConfig.label}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          {invitation.status === 'pending' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onCopy(invitation.code)}
                title="Copy link"
                data-testid={`copy-link-btn-${invitation.id}`}
              >
                <Copy size={16} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRevoke(invitation.id)}
                className="text-red-600"
                title="Revoke"
                data-testid={`revoke-btn-${invitation.id}`}
              >
                <XCircle size={16} />
              </Button>
            </>
          )}
          {['pending', 'expired'].includes(invitation.status) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onResend(invitation)}
              title="Resend"
              data-testid={`resend-btn-${invitation.id}`}
            >
              <ArrowClockwise size={16} />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

const InvitationsTable = ({
  invitations,
  loading,
  canManage,
  onCreate,
  onCopy,
  onRevoke,
  onResend,
}) => (
  <Card className="border-slate-200">
    <CardHeader className="pb-0">
      <CardTitle className="text-lg">Invitations</CardTitle>
    </CardHeader>
    <CardContent className="p-0">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : invitations.length === 0 ? (
        <EmptyState canManage={canManage} onCreate={onCreate} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[150px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invitations.map((invitation) => (
              <InvitationRow
                key={invitation.id}
                invitation={invitation}
                onCopy={onCopy}
                onRevoke={onRevoke}
                onResend={onResend}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);

export default InvitationsTable;
